import { FC, useEffect, useMemo, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import moment from "moment";

import { useData } from "data/data-provider";
import DatePickerCustomHeaderOneMonth from "../DatePickerCustomHeaderOneMonth";
import DatePickerCustomHeaderTwoMonth from "../DatePickerCustomHeaderTwoMonth";
import { useClickOutside } from "./hooks/useClickOutside";
import { useScreenSize } from "./hooks/useScreenSize";

interface HoursViewProps {
  // Mirror the props the Dates tab calendar receives so the Hours tab
  // calendar renders identically (availability underlines, disabled
  // unavailable dates, month-change fetching). All optional so the
  // component degrades gracefully if a parent doesn't pass them.
  isDateSelectable?: (date: Date) => boolean;
  renderDayContents?: (day: number, date?: Date) => React.ReactNode;
  handleMonthChange?: (date: Date) => void;
  calendarViewDate?: Date | null;
}

const HoursView: FC<HoursViewProps> = ({
  isDateSelectable,
  renderDayContents,
  handleMonthChange,
  calendarViewDate,
}) => {
  const {
    startDate,
    endDate,
    startTime,
    endTime,
    setStartDate,
    setEndDate,
    setStartTime,
    setEndTime,
    ourCompany,
    pickupLocation,
  } = useData();

  const { isLargeScreen } = useScreenSize();
  const monthsToShow = isLargeScreen ? 2 : 1;

  const use12HourFormat =
    ourCompany?.default_time_format === "12_hour" ||
    ourCompany?.company?.default_time_format === "12_hour";

  const [isPickupTimeOpen, setIsPickupTimeOpen] = useState(false);
  const [isDropoffTimeOpen, setIsDropoffTimeOpen] = useState(false);

  const pickupTimeRef = useRef<HTMLDivElement>(null);
  const dropoffTimeRef = useRef<HTMLDivElement>(null);

  useClickOutside(pickupTimeRef, isPickupTimeOpen, () =>
    setIsPickupTimeOpen(false),
  );
  useClickOutside(dropoffTimeRef, isDropoffTimeOpen, () =>
    setIsDropoffTimeOpen(false),
  );

  // Seed defaults: today 10:00 → today 16:00 (6h block) on first open
  useEffect(() => {
    if (!startDate) {
      setStartDate(new Date());
      setStartTime("10:00");
    }
    if (!endDate) {
      setEndDate(new Date());
      setEndTime("16:00");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Compute current pickup → dropoff gap in hours (for Quick Select highlight)
  const startMoment = useMemo(
    () =>
      startDate && startTime
        ? moment(
            `${moment(startDate).format("YYYY-MM-DD")} ${startTime}`,
            "YYYY-MM-DD HH:mm",
          )
        : null,
    [startDate, startTime],
  );
  const endMoment = useMemo(
    () =>
      endDate && endTime
        ? moment(
            `${moment(endDate).format("YYYY-MM-DD")} ${endTime}`,
            "YYYY-MM-DD HH:mm",
          )
        : null,
    [endDate, endTime],
  );

  // Current pickup → dropoff gap in whole hours, used to mark the active
  // Quick Select chip with a green ✓.
  const gapHours = useMemo(() => {
    if (!startMoment || !endMoment) return null;
    const diff = endMoment.diff(startMoment, "hours", true);
    return diff > 0 ? diff : null;
  }, [startMoment, endMoment]);

  // Quick Select durations (hours) per spec.
  const QUICK_SELECT = [2, 4, 6, 8, 12, 24];

  // Quick Select: drop-off = pickup + N hours
  const applyQuickSelect = (hours: number) => {
    if (!startMoment) return;
    const newEnd = startMoment.clone().add(hours, "hours");
    setEndDate(newEnd.toDate());
    setEndTime(newEnd.format("HH:mm"));
  };

  // 1-hour increments only (per spec)
  const timeOptions = Array.from({ length: 24 }, (_, h) =>
    `${h.toString().padStart(2, "0")}:00`,
  );

  // Block times outside the pickup location's operational hours — matches
  // the existing pattern in dateRangeSelection.tsx::isTimeBlocked.
  const opStart = pickupLocation?.operational_start_time ?? null;
  const opEnd = pickupLocation?.operational_end_time ?? null;
  const isTimeOperational = (time: string): boolean => {
    if (!opStart || !opEnd) return true;
    const t = moment(time, "HH:mm");
    return (
      !t.isBefore(moment(opStart, "HH:mm")) &&
      !t.isAfter(moment(opEnd, "HH:mm"))
    );
  };

  const formatTime = (time: string | null | undefined) => {
    if (!time) return "Time";
    const m = moment(time, "HH:mm");
    return use12HourFormat ? m.format("h:mm a") : m.format("HH:mm");
  };

  const formatDateChip = (date: Date | null | undefined) => {
    if (!date) return "Until";
    return moment(date).format("dddd, MMM D");
  };

  // Human-readable "Available 7:00 AM – 7:00 PM" caption so the renter knows
  // why some time slots are disabled (1NOW-977 QA UX). Accepts both "HH:mm"
  // and "HH:mm:ss" since the API serializes TimeField as the latter.
  const formatOpTime = (t: string) => {
    const m = moment(t, ["HH:mm:ss", "HH:mm"]);
    return use12HourFormat ? m.format("h:mm A") : m.format("HH:mm");
  };
  const operatingHoursLabel =
    opStart && opEnd
      ? `Available ${formatOpTime(opStart)} – ${formatOpTime(opEnd)}`
      : null;

  // Range calendar handler.
  // react-datepicker's selectsRange fires [start, null] on the first click of
  // a fresh range and [start, end] on the second. We mirror both — including
  // null end — so the previous endDate doesn't get stranded behind a new
  // start date (which would invert the range).
  const onRangeChange = (dates: [Date | null, Date | null] | null) => {
    if (!dates) return;
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  };

  return (
    <div className="px-2 pt-1 pb-2">
      {/* Pickup / Drop-off summary chip row */}
      <div className="flex flex-wrap items-center justify-center gap-2 p-3 rounded-2xl bg-secondary-100/30 mb-5">
        {/* Pickup date (read-only — calendar below drives this) */}
        <div className="px-4 py-2 bg-card-color rounded-lg text-sm font-medium text-text-color shadow-sm">
          {formatDateChip(startDate)}
        </div>

        {/* Pickup time */}
        <div className="relative" ref={pickupTimeRef}>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              setIsPickupTimeOpen((o) => !o);
              setIsDropoffTimeOpen(false);
            }}
            className="px-4 py-2 bg-card-color rounded-lg text-sm font-medium text-text-color shadow-sm hover:bg-secondary-100/40 transition-colors"
          >
            {formatTime(startTime)}
          </button>
          {isPickupTimeOpen && (
            <div
              className="absolute left-0 top-full mt-2 z-50 rounded-xl shadow-xl ring-1 ring-black/5 bg-card-color p-2 max-h-64 overflow-y-auto w-28"
              onMouseDown={(e) => e.stopPropagation()}
            >
              {timeOptions.map((h) => {
                const operational = isTimeOperational(h);
                const isSelected = startTime === h;
                return (
                  <button
                    key={h}
                    type="button"
                    disabled={!operational}
                    title={!operational ? "Outside operating hours" : undefined}
                    onClick={(e) => {
                      e.preventDefault();
                      if (!operational) return;
                      setStartTime(h);
                      setIsPickupTimeOpen(false);
                    }}
                    className={`block w-full text-left px-3 py-1.5 rounded-md text-sm transition-colors ${
                      isSelected
                        ? "bg-primary-200 text-on-primary font-semibold"
                        : !operational
                          ? "text-text-color/30 line-through cursor-not-allowed"
                          : "text-text-color hover:bg-secondary-100/40"
                    }`}
                  >
                    {formatTime(h)}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <span className="text-text-color/50 px-1 font-medium">–</span>

        {/* Drop-off time */}
        <div className="relative" ref={dropoffTimeRef}>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              setIsDropoffTimeOpen((o) => !o);
              setIsPickupTimeOpen(false);
            }}
            className="px-4 py-2 bg-card-color rounded-lg text-sm font-medium text-text-color shadow-sm hover:bg-secondary-100/40 transition-colors"
          >
            {formatTime(endTime)}
          </button>
          {isDropoffTimeOpen && (
            <div
              className="absolute left-0 top-full mt-2 z-50 rounded-xl shadow-xl ring-1 ring-black/5 bg-card-color p-2 max-h-64 overflow-y-auto w-28"
              onMouseDown={(e) => e.stopPropagation()}
            >
              {timeOptions.map((h) => {
                const operational = isTimeOperational(h);
                const isSelected = endTime === h;
                return (
                <button
                  key={h}
                  type="button"
                  disabled={!operational}
                  title={!operational ? "Outside operating hours" : undefined}
                  onClick={(e) => {
                    e.preventDefault();
                    if (!operational) return;
                    setEndTime(h);
                    setIsDropoffTimeOpen(false);
                  }}
                  className={`block w-full text-left px-3 py-1.5 rounded-md text-sm transition-colors ${
                    isSelected
                      ? "bg-primary-200 text-on-primary font-semibold"
                      : !operational
                        ? "text-text-color/30 line-through cursor-not-allowed"
                        : "text-text-color hover:bg-secondary-100/40"
                  }`}
                >
                  {formatTime(h)}
                </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Drop-off date (read-only — calendar below drives this) */}
        <div className="px-4 py-2 bg-card-color rounded-lg text-sm font-medium text-text-color shadow-sm">
          {formatDateChip(endDate)}
        </div>
      </div>

      {/* Operating-hours caption — explains why some time slots are disabled. */}
      {operatingHoursLabel && (
        <p className="text-center text-xs text-text-color/60 -mt-3 mb-4">
          {operatingHoursLabel}
        </p>
      )}

      {/* Quick Select chips — auto-set drop-off = pickup + N hours.
          Active chip (matching the current gap) shows a green ✓. */}
      <div className="flex flex-wrap items-center justify-center gap-2 mb-5">
        {QUICK_SELECT.map((hours) => {
          const isActive = gapHours === hours;
          return (
            <button
              key={hours}
              type="button"
              disabled={!startMoment}
              onClick={(e) => {
                e.preventDefault();
                applyQuickSelect(hours);
              }}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                isActive
                  ? "bg-primary-200 text-on-primary"
                  : "bg-card-color text-text-color shadow-sm hover:bg-secondary-100/40"
              }`}
            >
              {isActive && <span className="text-green-600 mr-1">✓</span>}
              {hours} {hours === 1 ? "hr" : "hrs"}
            </button>
          );
        })}
      </div>

      {/* Inline 2-month range calendar — same config as the Dates tab */}
      <div className="hourly-range-calendar w-full mb-5">
        <DatePicker
          selected={startDate}
          onChange={(dates) =>
            onRangeChange(dates as [Date | null, Date | null])
          }
          startDate={startDate}
          endDate={endDate}
          selectsRange
          monthsShown={monthsToShow}
          showPopperArrow={false}
          inline
          minDate={new Date()}
          openToDate={calendarViewDate || startDate || new Date()}
          filterDate={isDateSelectable}
          renderCustomHeader={(p) => (
            <>
              <div className="block md:hidden">
                <DatePickerCustomHeaderOneMonth {...p} />
              </div>
              <div className="hidden md:block">
                <DatePickerCustomHeaderTwoMonth {...p} />
              </div>
            </>
          )}
          renderDayContents={renderDayContents}
          onMonthChange={handleMonthChange}
        />
      </div>
    </div>
  );
};

export default HoursView;
